import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { useDiaries } from '@/hooks/useDiaries';
import { motion } from 'framer-motion';
import {
  BookOpen, Plus, Lock, Globe, Calendar, MapPin, Edit,
  Camera, Heart, Compass, ChevronRight, Sparkles
} from 'lucide-react';
import CatCompanion from '@/effects/cat/CatCompanion';
import OceanBreathing from '@/effects/ocean/OceanBreathing';
import OceanBackground from '@/components/background/OceanBackground';

/* ── ── ── 常量 ── ── ── */
const TRAVEL_IMGS = [
  'https://images.unsplash.com/photo-1528164344705-47542687000d?w=600&q=70',
  'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=70',
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=600&q=70',
  'https://images.unsplash.com/photo-1492571350019-22de08371fd3?w=600&q=70',
  'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=70',
  'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=600&q=70',
];

const MOODS = [
  { key: 'write', label: '写日记', icon: Edit, color: '#58B7E8' },
  { key: 'photo', label: '拍拍照', icon: Camera, color: '#a78bfa' },
  { key: 'place', label: '标地点', icon: MapPin, color: '#34d399' },
  { key: 'mood', label: '记心情', icon: Heart, color: '#f472b6' },
];

/* ── ── ── 入口动画常量 ── ── ── */
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.2 }
  }
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

/* ── ── ── 主页面 ── ── ── */
const Diaries = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { diaries, loading: diariesLoading } = useDiaries(user?.id);
  const [hoveredCard, setHoveredCard] = useState(null);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0f2240 50%, #0a1628 100%)' }}>
        <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderTopColor: 'var(--bloom)' }} />
      </div>
    );
  }

  const loading = authLoading || diariesLoading;
  const hasDiaries = diaries.length > 0;

  return (
    <div className="min-h-screen relative">
      {/* ══ 背景层 ══ */}
      <OceanBreathing
        tintColor="rgba(8,18,40,0.4)"
        waveColor="rgba(100,180,230,0.05)"
        glowColor="rgba(255,235,170,0.04)"
      />

      {/* 页面背景渐变 - 从深蓝到底部微亮 */}
      <div className="fixed inset-0 pointer-events-none" style={{
        background: 'linear-gradient(180deg, #0c1a30 0%, #0f2240 35%, #0e2845 65%, #0c1a30 100%)',
        zIndex: 0,
      }} />

      {/* 深海生物层 — 统一背景系统 */}
      <OceanBackground />

      <Navbar />

      <main className="relative z-10 pb-24" style={{ paddingTop: '100px' }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8">

          {/* ─── SECTION 1: Journey Hero ─── */}
          <motion.section
            variants={container}
            initial="hidden"
            animate="show"
            className="text-center mb-16"
          >
            <motion.div variants={item} className="mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px] mb-8"
                style={{ background: 'rgba(88,183,232,0.08)', border: '1px solid rgba(88,183,232,0.15)', color: '#7ec8e3' }}>
                <Sparkles className="w-3 h-3" />
                {hasDiaries ? '你的旅行日记' : '开始你的旅行故事'}
              </div>
            </motion.div>

            <motion.h1
              variants={item}
              className="font-semibold mb-4 leading-tight"
              style={{
                fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                color: '#f0f4f8',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              旅行日记
            </motion.h1>

            <motion.p variants={item} className="max-w-md mx-auto mb-10 leading-relaxed"
              style={{ color: 'rgba(158,200,224,0.75)', fontSize: '15px' }}>
              给未来的自己留一封信
            </motion.p>

            {/* 猫咪陪伴 — Hero 区域 */}
            <motion.div variants={item} className="mb-10">
              <CatCompanion size={80} mood="idle" />
              <p className="mt-3 text-[13px]" style={{ color: 'rgba(158,200,224,0.5)' }}>
                记录微小的幸福
              </p>
            </motion.div>

            {/* 核心 CTA */}
            <motion.div variants={item}>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/diary/new')}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl font-medium text-[14px] transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #58B7E8, #3d82b0)',
                  color: '#fff',
                  boxShadow: '0 4px 24px rgba(88,183,232,0.25)',
                }}
              >
                <Edit className="w-4 h-4" />
                写日记
              </motion.button>
            </motion.div>
          </motion.section>

          {/* ─── SECTION 2: 快速记录 ─── */}
          {!hasDiaries && (
            <motion.section
              variants={container}
              initial="hidden"
              animate="show"
              className="mb-20"
            >
              <motion.div variants={item}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
                  {MOODS.map((m) => {
                    const Icon = m.icon;
                    return (
                      <motion.button
                        key={m.key}
                        whileTap={{ scale: 0.96 }}
                        variants={item}
                        onClick={() => navigate(m.key === 'write' ? '/diary/new' : '/travelogues/new')}
                        className="flex flex-col items-center gap-3 p-5 rounded-2xl transition-all duration-200"
                        style={{
                          background: 'rgba(13,29,50,0.45)',
                          backdropFilter: 'blur(16px)',
                          WebkitBackdropFilter: 'blur(16px)',
                          border: '1px solid rgba(143,169,192,0.08)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = m.color + '33';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(143,169,192,0.08)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ background: m.color + '18' }}>
                          <Icon className="w-5 h-5" style={{ color: m.color }} />
                        </div>
                        <span className="text-[13px]" style={{ color: '#dce4ec' }}>{m.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            </motion.section>
          )}

          {/* ─── SECTION 3: 空状态 ─── */}
          {!hasDiaries && (
            <motion.section
              variants={container}
              initial="hidden"
              animate="show"
              className="text-center py-16 mb-12"
            >
              <motion.div variants={item}
                className="inline-flex items-center justify-center w-24 h-24 rounded-3xl mb-8"
                style={{ background: 'rgba(13,29,50,0.4)', border: '1px solid rgba(143,169,192,0.06)' }}
              >
                <CatCompanion size={100} mood="sleepy" />
              </motion.div>
              <motion.h2 variants={item} className="text-xl font-medium mb-3"
                style={{ color: '#e8eef4' }}>
                给未来的自己留一封信
              </motion.h2>

              <motion.div variants={item} className="flex flex-col sm:flex-row gap-3 justify-center">
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate('/diary/new')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-[14px] font-medium"
                  style={{
                    background: 'linear-gradient(135deg, #58B7E8, #3d82b0)',
                    color: '#fff',
                    boxShadow: '0 4px 20px rgba(88,183,232,0.2)',
                  }}
                >
                  <Plus className="w-4 h-4" />
                  开始第一段旅程
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate('/travelogues/new')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-[14px] font-medium"
                  style={{
                    background: 'rgba(13,29,50,0.55)',
                    border: '1px solid rgba(143,169,192,0.1)',
                    color: 'rgba(220,228,236,0.8)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  <Camera className="w-4 h-4" />
                  导入旅行照片
                </motion.button>
              </motion.div>
            </motion.section>
          )}

          {/* ─── SECTION 3b: 有日记 ─ 我的旅行 ─── */}
          {hasDiaries && (
            <motion.section
              variants={container}
              initial="hidden"
              animate="show"
              className="mb-16"
            >
              <motion.div variants={item} className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium" style={{ color: '#e8eef4' }}>
                  我的旅行
                </h2>
                <button onClick={() => navigate('/diary/new')}
                  className="text-[13px] flex items-center gap-1 transition-colors hover:opacity-80"
                  style={{ color: 'rgba(88,183,232,0.8)' }}>
                  查看全部 <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </motion.div>

              <motion.div variants={item} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {diaries.slice(0, 6).map((diary, idx) => (
                  <motion.button
                    key={diary.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(`/diary/${diary.id}`)}
                    className="group relative overflow-hidden rounded-2xl text-left cursor-pointer"
                    style={{ aspectRatio: '16/10' }}
                    onMouseEnter={() => setHoveredCard(diary.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    {/* 背景图 */}
                    <img
                      src={TRAVEL_IMGS[idx % TRAVEL_IMGS.length]}
                      alt={diary.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover card-img-zoom"
                    />
                    {/* 底部渐变 */}
                    <div className="absolute inset-0" style={{
                      background: 'linear-gradient(180deg, transparent 30%, rgba(8,16,32,0.95) 100%)',
                    }} />
                    {/* 内容 */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 card-content-rise">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-medium text-sm text-white line-clamp-1">
                          {diary.title || '未命名日记'}
                        </h3>
                        {diary.is_public !== false
                          ? <Globe className="w-3.5 h-3.5 text-[#58B7E8] shrink-0" />
                          : <Lock className="w-3.5 h-3.5 text-[#8898ab] shrink-0" />}
                      </div>
                      <p className="text-[11px] line-clamp-1" style={{ color: 'rgba(158,200,224,0.7)' }}>
                        {diary.created_at
                          ? new Date(diary.created_at).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '今天'}
                        {diary.location ? ` · ${diary.location}` : ''}
                      </p>
                    </div>
                    {/* 顶部微光（hover 时） */}
                    <div
                      className="absolute inset-0 pointer-events-none transition-opacity duration-300"
                      style={{
                        background: 'linear-gradient(180deg, rgba(88,183,232,0.08) 0%, transparent 30%)',
                        opacity: hoveredCard === diary.id ? 1 : 0,
                      }}
                    />
                  </motion.button>
                ))}
              </motion.div>
            </motion.section>
          )}

          {/* ─── SECTION 4: 有日记 ─ 最近日记时间线 ─── */}
          {hasDiaries && (
            <motion.section
              variants={container}
              initial="hidden"
              animate="show"
            >
              <motion.div variants={item} className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium" style={{ color: '#e8eef4' }}>
                  最近记录
                </h2>
                <button onClick={() => navigate('/diary/new')}
                  className="flex items-center gap-1 px-4 py-2 rounded-xl text-[13px] font-medium transition-all duration-200"
                  style={{
                    background: 'rgba(88,183,232,0.08)',
                    border: '1px solid rgba(88,183,232,0.12)',
                    color: '#7ec8e3',
                  }}>
                  <Plus className="w-3.5 h-3.5" />
                  新日记
                </button>
              </motion.div>

              <motion.div variants={item} className="space-y-3">
                {diaries.slice(0, 5).map((diary, idx) => (
                  <motion.button
                    key={diary.id}
                    variants={item}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => navigate(`/diary/${diary.id}`)}
                    className="w-full text-left flex items-center gap-4 p-4 rounded-2xl transition-all duration-200"
                    style={{
                      background: 'rgba(13,29,50,0.4)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(143,169,192,0.06)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(13,29,50,0.52)';
                      e.currentTarget.style.borderColor = 'rgba(143,169,192,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(13,29,50,0.4)';
                      e.currentTarget.style.borderColor = 'rgba(143,169,192,0.06)';
                    }}
                  >
                    {/* 日期指示器 */}
                    <div className="flex flex-col items-center shrink-0" style={{ width: 40 }}>
                      <span className="text-[11px]" style={{ color: 'rgba(88,183,232,0.7)' }}>
                        {diary.created_at ? new Date(diary.created_at).toLocaleDateString('zh-CN', { month: 'short' }) : '·'}
                      </span>
                      <span className="text-lg font-semibold" style={{ color: '#e8eef4' }}>
                        {diary.created_at ? new Date(diary.created_at).getDate() : '·'}
                      </span>
                    </div>

                    {/* 内容预览 */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate mb-1">
                        {diary.title || '未命名日记'}
                      </h3>
                      <p className="text-[12px] truncate" style={{ color: 'rgba(158,200,224,0.6)' }}>
                        {diary.content?.blocks?.[0]?.text || diary.summary || '暂无内容'}
                      </p>
                    </div>

                    {/* 右侧箭头 */}
                    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'rgba(158,200,224,0.3)' }} />
                  </motion.button>
                ))}
              </motion.div>
            </motion.section>
          )}

        </div>
      </main>
    </div>
  );
};

export default Diaries;
